import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Eye, EyeOff, User, Lock } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import medicalBg from "@/assets/medical-bg.jpg";

export function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  

  // Redirect if already authenticated
  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, loading]); // Removed navigate from dependencies

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const result = await login(email, password);
      
      if (result.success) {
        toast({
          title: "Login Successful",
          description: "Welcome to the Cellular Order Entry System",
        });
        // Redirect to main page after successful login
        navigate("/");
      } else {
        toast({
          title: "Login Failed", 
          description: "Please check your credentials and try again",
          variant: "destructive"
        });
      }
    } catch (error) {
      // Login error handled by toast
      toast({
        title: "Login Failed",
        description: error instanceof Error ? error.message : "Please check your credentials and try again",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center bg-cover bg-center bg-no-repeat relative"
      style={{ backgroundImage: `url(${medicalBg})` }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-medical-blue/20 backdrop-blur-sm" />
      
      <div className="relative z-10 w-full max-w-md p-6">
        <Card className="shadow-medical border-0 bg-white/95 backdrop-blur-sm">
          <CardHeader className="text-center pb-6">
            <div className="mb-4">
              <div className="flex justify-center mb-2">
                <img 
                  src="/lovable-uploads/5a6285bd-aaa9-4c5f-8b8f-a7c3b2e2f4d4.png" 
                  alt="PragLife - Pragmatrix Lifesciences"
                  className="h-16 w-auto"
                />
              </div>
              <div className="h-1 w-20 bg-gradient-primary rounded-full mx-auto" />
            </div>
            <CardTitle className="text-2xl text-medical-blue font-semibold">
              Cellular Order Entry System
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Secure access to cell therapy management platform
            </CardDescription>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium text-foreground">
                  Email (User ID)
                </Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter your email address"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-12 border-medical-blue/20 focus:border-medical-blue"
                    required
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-foreground">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 h-12 border-medical-blue/20 focus:border-medical-blue"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-medical-blue"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-4 pt-2">
                <Button
                  type="submit"
                  className="w-full h-12 bg-gradient-primary hover:opacity-90 text-white font-semibold rounded-lg shadow-medical transition-all duration-200"
                  disabled={isLoading}
                >
                  {isLoading ? "Signing In..." : "Login"}
                </Button>
                
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full text-medical-blue hover:text-medical-blue-dark hover:bg-medical-blue-light"
                >
                  Forgot Password?
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
        
        <div className="text-center mt-6">
          <img 
            src="/lovable-uploads/74f1d40f-d99f-4dab-af18-eca9251a8c20.png" 
            alt="Pragmatrix Software Life Sciences"
            className="h-8 w-auto mx-auto opacity-90"
          />
        </div>
      </div>
    </div>
  );
}